import React, { useEffect, useMemo, useRef } from 'react';
import { Container } from 'pixi.js';
import { useRecoilValue } from 'recoil';
import gsap from 'gsap';

import useLoader, { getTexture } from '@/game/useLoader';

import { GemState } from '@/framework/gem';

import {
  boardState,
  winningComboState,
  gamePhaseState,
} from '@/framework/board';

import { userPlayerState, opponentPlayerState } from '@/framework/player';

import Gem from './Gem';

import { mapPidToColor } from './utils';

export type Props = {
  width: number;
  height: number;
  y: number;

  /** Needs to have a correct zIndex, so that gems don't render being the cells */
  zIndex: number;
  /** Child of cell - ie. Gem */
  children?: React.ReactNode;
  cIndex: number;
  rIndex: number;
};

function DroppingGem({
  dropFrom,
  children,
}: {
  dropFrom: number;
  children: React.ReactNode;
}) {
  const ref = useRef<Container | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    node.y = -dropFrom;
    const tween = gsap.to(node, {
      y: 0,
      duration: 0.7,
      ease: 'bounce.out',
    });

    return () => {
      tween.kill();
    };
  }, [dropFrom]);

  return (
    <pixiContainer ref={ref} zIndex={10}>
      {children}
    </pixiContainer>
  );
}

const Cell = ({ width, height, zIndex, y, cIndex, rIndex }: Props) => {
  const { loaded } = useLoader();

  const cellState = useRecoilValue(boardState[cIndex][rIndex]);
  const winningCombo = useRecoilValue(winningComboState);
  const gamePhase = useRecoilValue(gamePhaseState);
  const isLast = cellState?.isLast;

  const userPlayer = {
    id: useRecoilValue(userPlayerState.id),
    colour: useRecoilValue(userPlayerState.colour),
  };

  const opponentPlayer = {
    id: useRecoilValue(opponentPlayerState.id),
    colour: useRecoilValue(opponentPlayerState.colour),
  };

  const gemState: GemState = useMemo(() => {
    if (winningCombo && gamePhase === 'over') {
      if (
        winningCombo?.some((cell) => cell[0] === cIndex && cell[1] === rIndex)
      ) {
        return 'selected';
      } else {
        return 'broken';
      }
    }

    if (isLast) {
      return 'selected';
    }

    return 'normal';
  }, [winningCombo, gamePhase, isLast]);

  const gemColor = useMemo(() => {
    if (!cellState) {
      return;
    }

    return mapPidToColor(cellState.pid, userPlayer, opponentPlayer);
  }, [userPlayer, opponentPlayer, cellState]);

  const isOpponent = !!cellState && cellState.pid === opponentPlayer.id;
  const rival =
    isOpponent &&
    !!userPlayer.colour &&
    userPlayer.colour === opponentPlayer.colour;

  const active = !!cellState;
  const dropFrom = y + height;

  if (!loaded) return null;

  const texture = getTexture('hole');
  const holeWidth = width;
  const holeHeight = height;

  return (
    <pixiContainer zIndex={zIndex} y={y} sortableChildren>
      <pixiSprite height={holeHeight} width={holeWidth} anchor={0} texture={texture} />

      {active && (
        <DroppingGem dropFrom={dropFrom}>
          <Gem
            width={width}
            height={height}
            type={gemColor}
            state={gemState}
            rival={rival}
          />
        </DroppingGem>
      )}
    </pixiContainer>
  );
};

export default Cell;
